import { defineStore } from 'pinia'
import { computed, inject, ref } from 'vue'
import type {
    IAssignedSubAccountData,
    IAssignedSubAccountSimple,
    IUpdateSubAccountAddressPayload,
} from '@/typings/subAccount'
import type { IAccountData } from '@/typings/account'
import { useToast } from 'primevue'
import { errorHandlerKey, servicesKey } from '@/config/keys.ts'
import i18n from '@/i18n'
import { onApiErrorHandler } from '@/helpers/backendErrors.ts'
import { DocumentToAcceptError } from '@/models/DocumentToAcceptError.ts'
import {
    CHILD_PROTECT_DOCUMENT,
    CURRENT_TERMS_DOCUMENT,
} from '@/config/requiredDocumentsCodes.ts'

export const useSubAccountStore = defineStore('subAccount', () => {
    const toast = useToast()
    const services = inject(servicesKey)
    const errorHandler = inject(errorHandlerKey)

    const subAccountsOptions = ref<IAssignedSubAccountSimple[]>([])
    const selectedSubAccountDetails = ref<null | IAssignedSubAccountData>(null)

    const isSubAccountActive = computed(() => Boolean(selectedSubAccountDetails.value?.isActive))

    const sendMessageIsActive = computed(() =>
        Boolean(selectedSubAccountDetails.value?.sendMessageIsActive),
    )

    const clearSubAccountData = (): void => {
        subAccountsOptions.value = []
        selectedSubAccountDetails.value = null
    }

    const clearSelectedSubAccountDetails = (): void => {
        selectedSubAccountDetails.value = null
    }

    const setSubAccountsOptionsFromAccountData = (accountData: IAccountData | null): void => {
        subAccountsOptions.value =
            accountData?.assignedSubaccounts?.map((subAccount) => {
                const fullName = `${subAccount.firstName || ''} ${subAccount.lastName || ''}`.trim()

                return {
                    subaccountId: subAccount.subaccountId,
                    fullName: fullName || subAccount.subaccountId,
                }
            }) || []
    }

    const loadSubAccountsOptions = async (): Promise<{
        isSuccess: boolean
        documentToAccept: typeof CHILD_PROTECT_DOCUMENT | null
        currentTermsNotAccepted: boolean
    }> => {
        try {
            const data = await services!.subAccount.getAssignedSubAccounts()

            if (!data.length) {
                throw new Error('[Invalid backend response] Empty assigned sub accounts list')
            }

            subAccountsOptions.value = data

            return { isSuccess: true, documentToAccept: null, currentTermsNotAccepted: false }
        } catch (err) {
            if (
                err instanceof DocumentToAcceptError &&
                err.documentToAccept === CURRENT_TERMS_DOCUMENT
            ) {
                subAccountsOptions.value = []

                return { isSuccess: false, documentToAccept: null, currentTermsNotAccepted: true }
            }

            onApiErrorHandler({
                err,
                toast,
                errorTitle: i18n.global.t('Error'),
                defaultErrorMessage: i18n.global.t(
                    'There was a problem downloading the list of people under your care',
                ),
                action: 'loadSubAccountsOptions',
                errorHandler,
            })
            subAccountsOptions.value = []

            const requiredDoc =
                err instanceof DocumentToAcceptError &&
                err.documentToAccept === CHILD_PROTECT_DOCUMENT
                    ? err.documentToAccept
                    : null

            return {
                isSuccess: false,
                documentToAccept: requiredDoc,
                currentTermsNotAccepted: false,
            }
        }
    }

    const fetchSubAccountDetailsById = async (
        subAccountId: string,
    ): Promise<IAssignedSubAccountData | null> => {
        try {
            const data = await services!.subAccount.getSubAccountDataById(subAccountId)

            if (!data) {
                throw new Error('Missing data from getSubAccountDataById')
            }

            selectedSubAccountDetails.value = data

            return data
        } catch (err) {
            selectedSubAccountDetails.value = null

            onApiErrorHandler({
                err,
                toast,
                errorTitle: i18n.global.t('Error'),
                defaultErrorMessage: i18n.global.t('There was a problem downloading data'),
                action: 'fetchSubAccountDetails',
                errorHandler,
            })

            return null
        }
    }

    const updateSubAccountAddress = async (
        subAccountId: string,
        payload: IUpdateSubAccountAddressPayload,
    ): Promise<boolean> => {
        const isSuccess = await services!.subAccount.updateSubAccountAddress(subAccountId, payload)

        if (isSuccess) {
            await fetchSubAccountDetailsById(subAccountId)
        }

        return isSuccess
    }

    return {
        subAccountsOptions,
        selectedSubAccountDetails,
        isSubAccountActive,
        sendMessageIsActive,
        loadSubAccountsOptions,
        setSubAccountsOptionsFromAccountData,
        clearSubAccountData,
        fetchSubAccountDetailsById,
        updateSubAccountAddress,
        clearSelectedSubAccountDetails,
    }
})
